import React from 'react';
import { AppTenantlessAuthedApiProvider } from '../components/App/AppTenantlessAuthedApi';
import { useTenantlessDeploymentContext } from '../components/App/AppTenantlessDeploymentProvider';
import {
    clearPostAuthReturn,
    consumeReturnAfterTenantSelected,
    legacyLoginCodeFromPath,
    legacyLoginCodeFromSavedReturn,
} from '../components/App/authReturnPath';
import GoTenantSelector from '../components/GoTenantSelector';
import type { TenantSelectorTenant } from '@equinoxx/react';
import EqxEnvironmentBanner from '../shared/components/EqxEnvironmentBanner/EqxEnvironmentBanner';
import EqxLoading from '../shared/components/EqxLoading';

const TENANT_ERROR_QUERY_PARAM = 'tenantError'
const USER_ERROR_QUERY_PARAM = 'userError'
const FORCE_SELECTOR_QUERY_PARAM = 'showSelector'

interface PageGetTenantProps { }
const PageGetTenant = function (props: PageGetTenantProps) {
    const [error, setError] = React.useState<string | undefined>(undefined)
    const [returnedFromError, setReturnedFromError] = React.useState(false)
    const [forceSelectorMode, setForceSelectorMode] = React.useState(false)
    const [navigatingToTenant, setNavigatingToTenant] = React.useState(false)
    const [legacyResolving, setLegacyResolving] = React.useState(false);
    const legacyAutoStarted = React.useRef(false);

    const deployment = useTenantlessDeploymentContext();

    React.useEffect(() => {
        //change body class - remove any that start with page-* and add page-login
        document.body.className = document.body.className.replace(/\bpage-\S+/g, '')
        document.body.classList.add('page-login')

        return () => {
            document.body.className = document.body.className.replace(/\bpage-\S+/g, '')
        }
    }, [])

    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const hasUserError = params.has(USER_ERROR_QUERY_PARAM)
        const hasTenantError = params.has(TENANT_ERROR_QUERY_PARAM)
        const hasForceSelector = params.has(FORCE_SELECTOR_QUERY_PARAM)
        if (!hasUserError && !hasTenantError && !hasForceSelector) return

        if (hasUserError || hasTenantError) {
            setReturnedFromError(true)
        }
        if (hasForceSelector) {
            setForceSelectorMode(true)
            // Explicit switch: land on the new org home, not the previous deep link.
            clearPostAuthReturn()
        }
        if (hasUserError) {
            setError('You do not have permission to log in to this app for this Organisation - contact support')
        } else if (hasTenantError) {
            setError('Organisation not valid for this app - please contact support')
        }
        params.delete(USER_ERROR_QUERY_PARAM)
        params.delete(TENANT_ERROR_QUERY_PARAM)
        params.delete(FORCE_SELECTOR_QUERY_PARAM)
        const nextQuery = params.toString()
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`
        window.history.replaceState({}, '', nextUrl)
    }, [])

    const goToTenant = (
        tenantDisplayID: string,
        selection?: { instance?: string },
        opts?: { skipResume?: boolean }
    ) => {
        const displayID = tenantDisplayID.trim()
        if (!displayID) {
            setError("Couldn't find that organisation, sorry. Please contact support.")
            setNavigatingToTenant(false)
            setLegacyResolving(false)
            return
        }
        const newInstance = (selection?.instance ?? '').trim()

        setError(undefined)
        setNavigatingToTenant(true)

        deployment.setInstance(newInstance)
        const enc = encodeURIComponent(displayID)
        const segment = newInstance ? `_${enc}-${newInstance}` : `_${enc}`
        const resume = opts?.skipResume
            ? null
            : consumeReturnAfterTenantSelected(segment)
        window.location.href = resume ?? `/${segment}/`
    }

    const onTenantSelected = async (
        tenant: TenantSelectorTenant,
        _environment: string,
        instance: string
    ) => {
        const displayID = tenant.displayID?.trim()
        if (!displayID) {
            setError("Couldn't find that organisation, sorry. Please contact support.")
            setNavigatingToTenant(false)
            return
        }
        return goToTenant(displayID, { instance })
    }

    // Legacy `/login/abc` (saved on redirect to `/switch`): resolve tenant without showing the picker.
    React.useEffect(() => {
        if (legacyAutoStarted.current) return
        const code =
            legacyLoginCodeFromSavedReturn() ??
            legacyLoginCodeFromPath(window.location.pathname)
        if (!code) return
        legacyAutoStarted.current = true
        clearPostAuthReturn()
        setLegacyResolving(true)
        goToTenant(code, { instance: '' }, { skipResume: true })
    }, [])

    if (legacyResolving || navigatingToTenant) {
        return <EqxLoading />
    }

    return (
        <>
            <EqxEnvironmentBanner
                environment={deployment.environment}
                instance={deployment.instance}
            />
            <div id="login-container">
                <div id="login">
                    <div id="login-header">
                        <img src="/images/logo-no-text.svg" alt="Continuum Go" id="login-logo" />
                        <img src="/images/logo-text.svg" alt="" id="login-logo-text" style={{ height: '20px' }} />
                    </div>
                    <div id="login-body" className="text-start p-0">
                        <h3 className="display-6 px-3 pt-3">Select Organisation</h3>
                        {error && (
                            <div className="alert alert-danger text-start mx-3 mt-3 mb-0" role="alert">
                                {error}
                            </div>
                        )}
                        <AppTenantlessAuthedApiProvider>
                            <GoTenantSelector
                                autoLoginIfOnlyOne={!(returnedFromError || forceSelectorMode)}
                                selectFunction={onTenantSelected}
                            />
                        </AppTenantlessAuthedApiProvider>
                    </div>
                </div>
            </div>
        </>
    )
}
export default PageGetTenant

